/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * PasswordStrengthIndicator Component
 * ============================================================================
 *
 * Visual indicator for password strength with a simple bar and suggestion.
 * Implements CoreX password policy (Path B).
 *
 * ============================================================================
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface PasswordStrengthIndicatorProps {
  password: string;
  /** Username to check against */
  username?: string;
  /** Email to check against */
  email?: string;
  /** Show detailed requirements */
  showRequirements?: boolean;
}

interface StrengthLevel {
  level: number;
  labelKey: string;
  color: string;
  suggestionKey: string;
}

const strengthLevels: StrengthLevel[] = [
  { level: 0, labelKey: 'tooWeak', color: '#DC2626', suggestionKey: 'addMoreChars' },
  { level: 1, labelKey: 'weak', color: '#DC2626', suggestionKey: 'addUppercaseNumbers' },
  { level: 2, labelKey: 'fair', color: '#F59E0B', suggestionKey: 'addSymbolSecurity' },
  { level: 3, labelKey: 'good', color: '#3B82F6', suggestionKey: 'addSymbolStronger' },
  { level: 4, labelKey: 'strong', color: '#10B981', suggestionKey: 'greatPassword' },
];

export function PasswordStrengthIndicator({
  password,
  username = '',
  email = '',
  showRequirements = false,
}: PasswordStrengthIndicatorProps) {
  const { t } = useTranslation('common');

  // Calculate password strength and check requirements
  const analysis = useMemo(() => {
    const checks = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      noUsername: !username || !password.toLowerCase().includes(username.toLowerCase()),
      noEmail: !email || !password.toLowerCase().includes(email.split('@')[0].toLowerCase()),
      noCommonWords: !/(password|corex|ventacorex|123456|qwerty)/i.test(password),
    };

    // Count character types present
    const typeCount = [
      checks.hasUppercase,
      checks.hasLowercase,
      checks.hasNumber,
      checks.hasSpecial,
    ].filter(Boolean).length;

    // Path B: 8-11 chars needs 3 of 4 types, 12+ chars needs 2 of 4 types
    const isLongPassword = password.length >= 12;
    const requiredTypes = isLongPassword ? 2 : 3;
    const meetsComplexity = typeCount >= requiredTypes;

    // Calculate strength level
    let strengthLevel = 0;
    if (password.length > 0) {
      if (checks.minLength) strengthLevel = 1;
      if (checks.minLength && typeCount >= 2) strengthLevel = 2;
      if (checks.minLength && meetsComplexity) strengthLevel = 3;
      if (checks.minLength && meetsComplexity && password.length >= 12) strengthLevel = 4;
      if (checks.minLength && checks.hasSpecial && typeCount >= 3) strengthLevel = 4;
      if (!checks.noUsername || !checks.noEmail || !checks.noCommonWords) {
        strengthLevel = Math.max(0, strengthLevel - 2);
      }
    }

    return {
      checks,
      typeCount,
      strengthLevel,
    };
  }, [password, username, email]);

  const currentStrength = strengthLevels[analysis.strengthLevel];
  // Calculate bar fill percentage (each level fills 25%)
  const fillPercent = (analysis.strengthLevel / 4) * 100;

  if (!password) {
    return null;
  }

  return (
    <div className="password-strength">
      {/* Strength bar */}
      <div className="strength-bar-track">
        <div
          className="strength-bar-fill"
          style={{
            width: `${fillPercent}%`,
            backgroundColor: currentStrength.color,
          }}
        />
      </div>

      {/* Suggestion text */}
      <p className="strength-suggestion">
        <span className="strength-label" style={{ color: currentStrength.color }}>
          {t(`passwordStrength.levels.${currentStrength.labelKey}`)}
        </span>
        {' - '}
        {t(`passwordStrength.suggestions.${currentStrength.suggestionKey}`)}
      </p>

      <style>{`
        .password-strength {
          margin-top: 8px;
          animation: fadeIn 0.2s ease-out;
        }

        .strength-bar-track {
          width: 100%;
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
          overflow: hidden;
        }

        [data-theme="light"] .strength-bar-track {
          background: #E2E8F0;
        }

        .strength-bar-fill {
          height: 100%;
          border-radius: 2px;
          transition: width 0.3s ease, background-color 0.3s ease;
        }

        .strength-suggestion {
          margin: 8px 0 0 0;
          font-size: 13px;
          color: #64748B;
        }

        .strength-label {
          font-weight: 500;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default PasswordStrengthIndicator;
